import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getErrorMessage,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import colorPallete, { elevation } from "@dana/MobiusOverrides/styles/variables";
import Carousel, { CarouselSlide } from "@dana/Overrides/Components/Carousel";
import React, { createRef, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    carouselType = "carouselType",
    showCategoryImage = "showCategoryImage",
    showCategoryName = "showCategoryName",
    showFeaturedCategories = "showFeaturedCategories"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.carouselType]: string;
        [fields.showCategoryImage]: boolean;
        [fields.showCategoryName]: boolean;
        [fields.showFeaturedCategories]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    errorMessage: getErrorMessage(state, ownProps.id),
    categories: getCategoriesDataView(state).value,
});

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface PrftTopCategoriesCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss; 
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss; 
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
}

export const PrftTopCategoriesCarouselStyles: PrftTopCategoriesCarouselStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        forwardAs: "div",
        css: css`
            text-align: center;
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`           
            [class*="GridOffset"] {
                @media only screen and (max-width: 768px) {
                    justify-content: flex-end;
                }
            max-width: 1420px;
            margin-left: auto;
            margin-right: auto;

                .btn-secondary {
                    margin-bottom: 4px;
                }
            }
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
            @media only screen and (max-width: 768px) {
                display: none;
            }
        `,
    },
    prevArrowButton: {
        shape: "rounded",
        css: css`
            padding: 0;
            min-width: 40px;
            border: 1px solid ${colorPallete.grey200};
            background: ${colorPallete.white};
            color: ${colorPallete.grey800};
            z-index: 2;
            box-shadow: ${elevation.dp04};

            [class*="ButtonIcon"] {
                color: ${colorPallete.grey800};
            }

            &:not(:disabled):hover {
                background: ${colorPallete.white};
                color: ${colorPallete.black};
                border: 1px solid ${colorPallete.grey100};

                [class*="ButtonIcon"] {
                    color: ${colorPallete.black};
                }
            }
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
            @media only screen and (max-width: 768px) {
                display: none;
            }
        `,
    },
    nextArrowButton: {
        shape: "rounded",
        css: css`
            padding: 0;
            min-width: 40px;
            border: 1px solid ${colorPallete.grey200};
            background: ${colorPallete.white};
            color: ${colorPallete.grey800};
            z-index: 2;
            box-shadow: ${elevation.dp04};

            [class*="ButtonIcon"] {
                color: ${colorPallete.grey800};
            }

            &:not(:disabled):hover {
                background: ${colorPallete.white};
                color: ${colorPallete.black};
                border: 1px solid ${colorPallete.grey100};

                [class*="ButtonIcon"] {
                    color: ${colorPallete.black};
                }
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            padding: 24px 0 24px 0;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            background: ${colorPallete.white};
            box-shadow: 0px 0px 0px 4px rgba(0,0,0,0.10);
            max-width: 243px;
            margin-left: 16px;

            @media (max-width: 1024px) {
                max-width: 220px;
            }

            @media only screen and (max-width: 768px) {
                min-width: 220px;
            }
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 8px 0;
            padding-bottom: 16px;
            display: flex;
            flex-direction: column;

            [data-test-selector="productImage"] {
                padding: 16px;
            }
            > div {
                display: flex;
                flex-direction: column;
                height: 100%;
            }
        `,
    },
    container: {
        css: css`
            display: flex;
            max-width: 1200px;
            flex-wrap: wrap;
            flex-direction: row;
            align-items: flex-start;
            justify-content: center;
            padding: 5px;
        `,
    },
    categoryImageItem: {
        width: 12,
        css: css`
            height: 160px;
            display: flex;
            justify-content: center;
        `,
    },
    categoryImage: {
        width: "145px",
        height: "145px",
    },
    categoryNameLinkItem: {
        width: 12,
        css: css`
            width: 100%;
            text-align: left;
            padding-bottom: 8px;
            text-align: center;
        `,
    },
    categoryNameLink: {
        css: css`
            width: 100%;
        `,
        typographyProps: {
            css: css`
                width: 100%;
            `,
        },
    },
    categoryName: {
        css: css`
            color: #363636;
            font-weight: bold;
            width: 100%;
        `,
    }
    
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const styles = PrftTopCategoriesCarouselStyles;

const PrftTopCategoriesCarousel = ({
    id,
    fields,
    errorMessage,
    shellContext,
    categories,
    loadCategories,
}: Props) => {
    const [slides, setSlides] = useState<CarouselSlide[]>([]);
    const afterCarousel = createRef<HTMLSpanElement>();
    const [areCategoriesAvailable, setAreCategoriesAvailable] = useState(false);
    
    useEffect(() => {
        if (!categories || categories.length === 0) {
            loadCategories();
        }
    }, [categories, loadCategories]);
    
    useEffect(() => {
        if (!categories || categories.length === 0) {
            setAreCategoriesAvailable(false);
            return;
        }
 
        const filteredCategories = fields.showFeaturedCategories
            ? categories.filter(o => o.isFeatured)
            : categories;
 
        if (filteredCategories.length > 0) {
            setAreCategoriesAvailable(true);
            setSlides(mapProductsToSlides(filteredCategories));
        } else {
            setAreCategoriesAvailable(false);
        }
 
    }, [categories, fields.showFeaturedCategories]);

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load ${fields.title || "Featured Categories Carousel"}. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    const mapProductsToSlides = (categories: Category[]) =>
        categories.map(category => ({
            id,
            renderComponent: function PrftCategoryCarouselCategoryRenderer() {
                return (
                    <div>
                        <GridContainer {...styles.container} id={`${category.id}-category-container`}>
                            {fields.showCategoryImage && (
                                <GridItem {...styles.categoryImageItem}>
                                    {category.path && (
                                        <Link href={category.path} title={category.shortDescription} {...styles.categoryImageLink}>
                                            <LazyImage altText={category.shortDescription} src={category.smallImagePath} {...styles.categoryImage} />
                                        </Link>
                                    )}
                                </GridItem>
                            )}
                            {fields.showCategoryImage && (
                                <GridItem {...styles.categoryNameLinkItem}>
                                    <Link href={category.path} title={category.shortDescription} {...styles.categoryNameLink}>
                                        <Typography {...styles.categoryName}>{translate(category.shortDescription)}</Typography>
                                    </Link>
                                </GridItem>
                            )}
                        </GridContainer>
                    </div>
                );
            },
        }));

  
    return (
        <>
        {areCategoriesAvailable &&         
        (<StyledWrapper {...styles.mainContainer}>            
                <SkipNav
                    text={translate("Skip Carousel")}
                    extendedStyles={styles.skipCarouselButton}
                    destination={afterCarousel}
                />
                <StyledWrapper {...styles.carouselContainer}>
                    <Carousel
                        id="TopCategoryCarousel"
                        maxNumberOfColumns={5}
                        title={translate(fields.title || "Featured Categories")}
                        slides={slides}
                        extendedStyles={PrftTopCategoriesCarouselStyles}
                    />
                </StyledWrapper>
            
        </StyledWrapper>
        )}
        </>
    );
};

const CarouselSlide = styled.div`
    padding: 0;
    position: relative;
    flex: 0 0 auto;
`;

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(PrftTopCategoriesCarousel))),
    definition: {
        group: "Common",
        icon: "gallery-thumbnails",
        displayName: "Perficient Top Categories Carousel",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                sortOrder: 0,
                tab: basicTab,
            },
            {
                name: fields.showCategoryName,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "Contextual",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.showCategoryImage,
                displayName: "Show Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "Contextual",
                sortOrder: 2,
                tab: basicTab,
            },
            {
                name: fields.showFeaturedCategories,
                displayName: "Show Featured Categories Only",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "Contextual",
                sortOrder: 3,
                tab: basicTab,
            }
        ],
    },
};

export default widgetModule;
